import React, { useState, useEffect, useCallback } from 'react';
import { getCourierOrders, dispatchOrder, markOrderDelivered, markOrderReturned, getCouriers, assignCourierToOrder, getMe } from '../../services/api';
import { formatCurrency } from '../../utils/currency';
import { useToast } from '../../contexts/ToastContext';

function Courier() {
  const { showSuccess, showError } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderDetailOpen, setIsOrderDetailOpen] = useState(false);
  const [dispatchingOrder, setDispatchingOrder] = useState(null);
  const [markingDelivered, setMarkingDelivered] = useState(null);
  const [user, setUser] = useState(null);
  const [couriers, setCouriers] = useState([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [orderToAssign, setOrderToAssign] = useState(null);
  const [selectedCourierId, setSelectedCourierId] = useState('');
  const [assigningCourier, setAssigningCourier] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [orderToReturn, setOrderToReturn] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [returningOrder, setReturningOrder] = useState(false);
  const [deliveryProof, setDeliveryProof] = useState({
    image: null,
    imagePreview: null,
    notes: '',
    location: '',
  });

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        return;
      }
      const data = await getCourierOrders(token);
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch courier orders:', error);
      showError(error.message || 'Failed to load orders. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    // Fetch current user to check if admin
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const userData = await getMe(token);
          setUser(userData);
          
          // If user is admin, fetch couriers for assignment
          const isAdmin = userData?.role?.name === 'super_admin' || userData?.role?.name === 'administrator' || userData?.role?.name === 'manager';
          if (isAdmin) {
            try {
              const couriersData = await getCouriers(token);
              setCouriers(Array.isArray(couriersData) ? couriersData : []);
            } catch (error) {
              console.error('Failed to fetch couriers:', error);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    
    fetchUser();
    fetchOrders();
    // Refresh every 30 seconds
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);
  
  const isAdmin = user?.role?.name === 'super_admin' || user?.role?.name === 'administrator' || user?.role?.name === 'manager';
  const isCourier = user?.role?.name === 'courier';

  const handleDispatch = async (orderId) => {
    // If admin, show assignment modal first
    if (isAdmin) {
      setOrderToAssign(orderId);
      setIsAssignModalOpen(true);
      return;
    }
    
    // If courier, dispatch directly
    setDispatchingOrder(orderId);
    try {
      const token = localStorage.getItem('auth_token');
      const result = await dispatchOrder(orderId, token);
      showSuccess(`Order dispatched! OTP: ${result.otp}. Customer has been notified.`);
      await fetchOrders();
    } catch (error) {
      console.error('Failed to dispatch order:', error);
      showError(error.message || 'Failed to dispatch order.');
    } finally {
      setDispatchingOrder(null);
    }
  };

  const handleAssignCourier = async () => {
    if (!selectedCourierId || !orderToAssign) {
      showError('Please select a courier.');
      return;
    }

    setAssigningCourier(true);
    try {
      const token = localStorage.getItem('auth_token');
      await assignCourierToOrder(orderToAssign, selectedCourierId, token);
      showSuccess('Order assigned to courier successfully!');
      setIsAssignModalOpen(false);
      setOrderToAssign(null);
      setSelectedCourierId('');
      await fetchOrders();
    } catch (error) {
      console.error('Failed to assign courier:', error);
      showError(error.message || 'Failed to assign courier to order.');
    } finally {
      setAssigningCourier(false);
    }
  };

  const handleMarkReturned = async () => {
    if (!orderToReturn) return;

    setReturningOrder(true);
    try {
      const token = localStorage.getItem('auth_token');
      await markOrderReturned(orderToReturn.id, returnReason, token);
      showSuccess('Order marked as returned. Customer has been notified. Order is now available for another courier.');
      setIsReturnModalOpen(false);
      setOrderToReturn(null);
      setReturnReason('');
      await fetchOrders();
    } catch (error) {
      console.error('Failed to mark order as returned:', error);
      showError(error.message || 'Failed to mark order as returned.');
    } finally {
      setReturningOrder(false);
    }
  };

  const handleViewDetails = (order) => {
    setIsOrderDetailOpen(true);
    
    // Use existing order data directly - courierOrders endpoint already includes
    // order items, user, and courier relationships, so no need to fetch again
    // This avoids 404 errors since /api/orders/{id} filters by user_id (customer)
    // and couriers can't access orders via that endpoint
    setSelectedOrder(order);
  };

  const handleMarkDelivered = async () => {
    if (!selectedOrder) return;
    
    if (!deliveryProof.notes) {
      showError('Please enter the OTP.');
      return;
    }

    setMarkingDelivered(selectedOrder.id);
    try {
      const token = localStorage.getItem('auth_token');
      await markOrderDelivered(selectedOrder.id, {
        delivery_proof_image: deliveryProof.image,
        notes: deliveryProof.notes,
        delivery_location: deliveryProof.location,
      }, token);
      showSuccess('Order marked as delivered successfully!');
      setIsOrderDetailOpen(false);
      setSelectedOrder(null);
      setDeliveryProof({
        image: null,
        imagePreview: null,
        notes: '',
        location: '',
      });
      await fetchOrders();
    } catch (error) {
      console.error('Failed to mark order as delivered:', error);
      showError(error.message || 'Failed to mark order as delivered.');
    } finally {
      setMarkingDelivered(null);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDeliveryProof({
          ...deliveryProof,
          image: reader.result,
          imagePreview: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (order) => {
    if (order.delivered_at) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border border-gray-300 shadow-sm">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          Delivered
        </span>
      );
    }
    if (order.dispatched_at) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-300 shadow-sm">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Dispatched
        </span>
      );
    }
    if (order.courier_id) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border border-yellow-300 shadow-sm">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          Assigned
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-300 shadow-sm">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
        Available
      </span>
    );
  };

  // Available orders: orders not assigned to any courier (available for assignment/dispatch)
  const availableOrders = orders.filter(o => !o.courier_id);
  
  // My orders: orders assigned to current courier (either assigned but not dispatched, or dispatched but not delivered)
  // For admins: shows all orders assigned to any courier (they can see everything)
  const myOrders = orders.filter(o => {
    if (isAdmin) {
      // Admin sees all assigned orders (dispatched or just assigned)
      return o.courier_id && !o.delivered_at;
    } else {
      // Couriers see only their assigned orders
      return o.courier_id === user?.id && !o.delivered_at;
    }
  });

  return (
    <div className="relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary/5 via-transparent to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-secondary/5 via-transparent to-transparent rounded-full blur-3xl"></div>
      </div>

      {/* Header Section */}
      <div className="mb-6 sm:mb-8 relative">
        <div className="flex items-center gap-2 sm:gap-3 mb-3">
          <div className="w-1 sm:w-1.5 h-8 sm:h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full shadow-lg shadow-primary/30"></div>
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight drop-shadow-sm">Courier Dashboard</h1>
            <p className="text-xs sm:text-sm text-gray-600 font-medium mt-0.5 sm:mt-1">Manage order deliveries and track shipments</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary/20 border-t-primary"></div>
            <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-4 border-primary/30"></div>
          </div>
        </div>
      ) : (
        <div className="space-y-6 sm:space-y-8">
          {/* Available Orders */}
          {availableOrders.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-1 h-6 sm:h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Available Orders</h2>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold border border-blue-300">
                  {availableOrders.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                {availableOrders.map((order) => (
                  <div key={order.id} className="relative bg-gradient-to-br from-white via-blue-50/20 to-white rounded-lg border border-blue-200/40 p-3 shadow-md shadow-blue-200/10 hover:shadow-lg hover:shadow-blue-300/20 hover:-translate-y-0.5 transition-all duration-200 group overflow-hidden">
                    {/* Top accent line */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-sm text-gray-900 truncate flex-1">{order.order_number}</h3>
                        {getStatusBadge(order)}
                      </div>
                      
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-gray-900 truncate">{order.user?.name || 'N/A'}</p>
                        <p className="text-xs text-gray-500 truncate">{order.phone || order.user?.email || ''}</p>
                      </div>
                      
                      <div className="mb-2.5 flex items-center justify-between">
                        <span className="text-xs text-gray-500">Total</span>
                        <span className="font-bold text-sm text-primary">{formatCurrency(order.total)}</span>
                      </div>
                      
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => handleViewDetails(order)}
                          className="flex-1 px-2 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold text-xs transition-colors"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDispatch(order.id)}
                          disabled={dispatchingOrder === order.id || order.courier_id}
                          className="flex-1 px-2 py-1.5 bg-gradient-to-r from-primary to-secondary text-white rounded-md font-semibold text-xs hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {dispatchingOrder === order.id ? (
                            <svg className="animate-spin h-3 w-3 mx-auto" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : order.courier_id ? (
                            'Assigned'
                          ) : isAdmin ? (
                            'Assign'
                          ) : (
                            'Dispatch'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* My Orders (Assigned/Dispatched) */}
          {myOrders.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-1 h-6 sm:h-8 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full"></div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">My Active Deliveries</h2>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold border border-green-300">
                  {myOrders.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                {myOrders.map((order) => (
                  <div key={order.id} className="relative bg-gradient-to-br from-white via-green-50/20 to-white rounded-lg border border-green-200/40 p-3 shadow-md shadow-green-200/10 hover:shadow-lg hover:shadow-green-300/20 hover:-translate-y-0.5 transition-all duration-200 group overflow-hidden">
                    {/* Top accent line */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-600"></div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-sm text-gray-900 truncate flex-1">{order.order_number}</h3>
                        {getStatusBadge(order)}
                      </div>
                      
                      {/* Only show OTP if order is dispatched (has dispatched_at) */}
                      {order.dispatched_at && order.delivery_otp && (
                        <div className="mb-2 p-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-md border border-blue-200/50">
                          <p className="text-xs font-semibold text-gray-600 mb-1 text-center">OTP</p>
                          <p className="font-mono font-bold text-xl text-blue-700 text-center tracking-wider">{order.delivery_otp}</p>
                        </div>
                      )}
                      
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-gray-900 truncate">{order.user?.name || 'N/A'}</p>
                        <p className="text-xs text-gray-500 truncate">{order.phone || order.user?.email || ''}</p>
                        {order.dispatched_at && (
                          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Dispatched: {formatDate(order.dispatched_at)}
                          </p>
                        )}
                      </div>
                      
                      {order.dispatched_at ? (
                        // Already dispatched - show "Mark Delivered" and "Mark Returned" buttons
                        <div className="flex flex-col gap-1.5">
                          <button
                            onClick={() => handleViewDetails(order)}
                            className="w-full px-2 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-md font-semibold text-xs hover:shadow-md transition-all flex items-center justify-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Mark Delivered
                          </button>
                          <button
                            onClick={() => {
                              setOrderToReturn(order);
                              setIsReturnModalOpen(true);
                            }}
                            className="w-full px-2 py-1.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-md font-semibold text-xs hover:shadow-md transition-all flex items-center justify-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Mark Returned
                          </button>
                        </div>
                      ) : (
                        // Assigned but not dispatched - show "Dispatch" button (only for couriers)
                        isCourier && (
                          <button
                            onClick={() => handleDispatch(order.id)}
                            disabled={dispatchingOrder === order.id}
                            className="w-full px-2 py-1.5 bg-gradient-to-r from-primary to-secondary text-white rounded-md font-semibold text-xs hover:shadow-md transition-all flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {dispatchingOrder === order.id ? (
                              <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Dispatch
                              </>
                            )}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {availableOrders.length === 0 && myOrders.length === 0 && (
            <div className="relative bg-gradient-to-br from-white via-gray-50/50 to-white rounded-2xl border-2 border-gray-200/50 shadow-xl p-12 sm:p-16 text-center overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-secondary/5 to-transparent rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No Orders Available</h3>
                <p className="text-gray-600 font-medium max-w-md mx-auto">All orders have been dispatched or there are no new shipments ready for delivery.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Order Detail Modal */}
      {isOrderDetailOpen && selectedOrder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-modal-pop">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-6 py-5 flex items-center justify-between sticky top-0 z-10 shadow-lg shadow-primary/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Order Details</h2>
                  <p className="text-xs text-white/80">{selectedOrder.order_number}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsOrderDetailOpen(false);
                  setSelectedOrder(null);
                  setDeliveryProof({
                    image: null,
                    imagePreview: null,
                    notes: '',
                    location: '',
                  });
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3">Customer Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-semibold">Name:</span> {selectedOrder.user?.name || 'N/A'}</div>
                    <div><span className="font-semibold">Email:</span> {selectedOrder.user?.email || 'N/A'}</div>
                    <div><span className="font-semibold">Phone:</span> {selectedOrder.phone || 'N/A'}</div>
                  </div>
                </div>

                {/* Order Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3">Order Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-semibold">Date:</span> {formatDate(selectedOrder.created_at)}</div>
                    {selectedOrder.tracking_number && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Tracking #:</span>
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 rounded-lg border border-green-300">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-mono font-bold text-green-700 text-xs">{selectedOrder.tracking_number}</span>
                        </div>
                      </div>
                    )}
                    {selectedOrder.delivery_otp && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs font-semibold text-gray-600 mb-1">Delivery OTP:</p>
                        <p className="font-mono font-bold text-2xl text-blue-700">{selectedOrder.delivery_otp}</p>
                      </div>
                    )}
                    <div><span className="font-semibold">Total:</span> {formatCurrency(selectedOrder.total)}</div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-gray-900 mb-2">Shipping Address</h3>
                <p className="text-sm text-gray-700 whitespace-pre-line">{selectedOrder.shipping_address || 'N/A'}</p>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-4">Order Items</h3>
                {(() => {
                  // Handle both camelCase and snake_case from API
                  // Laravel returns snake_case by default
                  const orderItems = selectedOrder.order_items || selectedOrder.orderItems || [];
                  const itemsCount = Array.isArray(orderItems) ? orderItems.length : 0;
                  return itemsCount > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {orderItems.map((item, index) => (
                        <div key={item.id || index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="w-full aspect-square rounded-lg overflow-hidden bg-gray-200 mb-2">
                            <img 
                              src={item.product?.images?.[0] || item.product?.image || 'https://via.placeholder.com/100'} 
                              alt={item.product?.name || 'Product'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/100';
                              }}
                            />
                          </div>
                          <h4 className="font-semibold text-gray-900 text-xs mb-1 line-clamp-2">{item.product?.name || 'Product'}</h4>
                          <div className="text-xs text-gray-600">
                            <div>Qty: {item.quantity}</div>
                            <div className="font-semibold text-gray-900 mt-0.5">{formatCurrency(item.subtotal)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-sm">No items found</p>
                  );
                })()}
              </div>

              {/* Delivery Proof Form (only if dispatched) */}
              {selectedOrder.dispatched_at && !selectedOrder.delivered_at && (
                <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-4">Delivery Proof</h3>
                  
                  {/* Image Upload */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Delivery Proof Image <span className="text-gray-400 font-normal">(Optional - Auto-deleted after 15 days)</span>
                    </label>
                    {deliveryProof.imagePreview ? (
                      <div className="relative">
                        <img
                          src={deliveryProof.imagePreview}
                          alt="Delivery proof"
                          className="w-full h-32 sm:h-40 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          onClick={() => setDeliveryProof({ ...deliveryProof, image: null, imagePreview: null })}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 sm:h-40 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-3 pb-4">
                          <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="mb-1 text-xs sm:text-sm text-gray-500 font-semibold">Click to upload photo</p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>

                  {/* Enter OTP */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Enter OTP <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={deliveryProof.notes}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                        setDeliveryProof({ ...deliveryProof, notes: value });
                      }}
                      placeholder="Enter 6-digit OTP"
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-center text-lg font-mono tracking-widest"
                      maxLength="6"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">Enter the 6-digit OTP sent to the customer for confirmation</p>
                  </div>

                  {/* Delivery Location */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Delivery Location
                    </label>
                    <input
                      type="text"
                      value={deliveryProof.location}
                      onChange={(e) => setDeliveryProof({ ...deliveryProof, location: e.target.value })}
                      placeholder="Address or GPS coordinates"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setIsOrderDetailOpen(false);
                  setSelectedOrder(null);
                  setDeliveryProof({
                    image: null,
                    imagePreview: null,
                    notes: '',
                    location: '',
                  });
                }}
                className="px-6 py-2 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
              {selectedOrder.dispatched_at && !selectedOrder.delivered_at && (
                <>
                  <button
                    onClick={() => {
                      setOrderToReturn(selectedOrder);
                      setIsReturnModalOpen(true);
                      setIsOrderDetailOpen(false);
                    }}
                    className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Mark Returned
                  </button>
                  <button
                    onClick={handleMarkDelivered}
                    disabled={markingDelivered === selectedOrder.id || !deliveryProof.notes || deliveryProof.notes.length !== 6}
                    className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {markingDelivered === selectedOrder.id ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Marking...
                      </span>
                    ) : (
                      'Mark as Delivered'
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assign Courier Modal (for admins) */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-modal-pop">
            <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-6 py-5 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-white">Assign Order to Courier</h2>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Select Courier <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCourierId}
                  onChange={(e) => setSelectedCourierId(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  disabled={assigningCourier}
                >
                  <option value="">-- Select a courier --</option>
                  {couriers.map((courier) => (
                    <option key={courier.id} value={courier.id}>
                      {courier.name} ({courier.email})
                    </option>
                  ))}
                </select>
                {couriers.length === 0 && (
                  <p className="mt-2 text-sm text-gray-500">No couriers available. Please create a courier user first.</p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsAssignModalOpen(false);
                    setOrderToAssign(null);
                    setSelectedCourierId('');
                  }}
                  disabled={assigningCourier}
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignCourier}
                  disabled={assigningCourier || !selectedCourierId}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold hover:shadow-xl hover:shadow-primary/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assigningCourier ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Assigning...
                    </span>
                  ) : (
                    'Assign Order'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark Order as Returned Modal */}
      {isReturnModalOpen && orderToReturn && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-modal-pop">
            <div className="bg-gradient-to-r from-orange-500 to-red-600 px-6 py-5 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-white">Mark Order as Returned</h2>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Unable to Deliver Order?</h3>
                <p className="text-gray-600 text-center mb-4">
                  This order will be returned and made available for another courier to pick up. The customer will be notified.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-1">Order: {orderToReturn.order_number}</p>
                  <p className="text-xs text-gray-600">Customer: {orderToReturn.user?.name || 'N/A'}</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Reason for Return <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <textarea
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="E.g., Customer not available, Wrong address, etc."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all resize-none"
                  rows="3"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsReturnModalOpen(false);
                    setOrderToReturn(null);
                    setReturnReason('');
                  }}
                  disabled={returningOrder}
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkReturned}
                  disabled={returningOrder}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-semibold hover:shadow-xl hover:shadow-orange-500/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {returningOrder ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Mark as Returned'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Courier;

